import { createClient } from "@supabase/supabase-js";

// Supabase project configuration from Vite env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // Fail fast to avoid accidentally using wrong project
  throw new Error("Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY");
}

// detectSessionInUrl is intentionally disabled: AuthCallback owns the PKCE
// code -> session exchange (see frontend/src/pages/auth/AuthCallback.jsx).
// If left at its default (true), the client races AuthCallback by auto-
// consuming the `code_verifier` from localStorage, causing a 400
// "both auth code and code verifier should be non-empty" on
// exchangeCodeForSession.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    detectSessionInUrl: false,
    flowType: "pkce",
    persistSession: true,
    autoRefreshToken: true,
  },
});

// Module-level in-flight latch for signInWithGoogle (see its docblock).
let signInWithGoogleInFlight = false;

// Auth helper functions
export const authHelpers = {
  // Sign up new user
  signUp: async (email, password, userData) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData, // Store additional user data
      },
    });
    return { data, error };
  },

  // Sign in existing user
  signIn: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    try {
      if (typeof window !== "undefined") {
        localStorage.removeItem("auth_token");
        sessionStorage.clear();
      }
    } catch {}
    return { error };
  },

  // Get current user
  getCurrentUser: () => {
    return supabase.auth.getUser();
  },

  // Listen to auth changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  // Sign in with Google OAuth.
  //
  // One initiation at a time: starting a SECOND OAuth flow overwrites the
  // stored PKCE state/code_verifier, so whichever redirect completes against
  // the older state lands back as `bad_oauth_state` ("OAuth state not found
  // or expired") — observed doing exactly that to brand-new players
  // mid-first-checkout (2026-08-13). A double-tap on the Google button is all
  // it takes. The flag auto-resets after 15s so a genuinely failed initiation
  // (popup blocked, network error) can be retried.
  signInWithGoogle: async () => {
    if (signInWithGoogleInFlight) {
      return { data: null, error: null };
    }
    signInWithGoogleInFlight = true;
    setTimeout(() => {
      signInWithGoogleInFlight = false;
    }, 15000);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    return { data, error };
  },
};
